import { useEffect, useState, useCallback } from 'react';
import { useAppSelector } from '../stores';
import MicrosoftService from '../services/api/microsoftService';

export interface BillingRecommendation {
    recommended_provider: 'stripe' | 'microsoft';
    message?: string;
    marketplace_url?: string;
    allow_stripe: boolean;
    reason: string;
}

export function useBillingRecommendation() {
    const auth = useAppSelector((state) => state.auth);
    const companyId = auth.session?.company_id;
    const accessToken = auth.access_token;
    const sessionMicrosoftTenantId = auth.session?.microsoft_tenant_id?.trim();

    const [recommendation, setRecommendation] = useState<BillingRecommendation | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!companyId || !accessToken) {
            setRecommendation(null);
            return;
        }

        let cancelled = false;

        const load = async () => {
            setLoading(true);
            try {
                const data = await MicrosoftService.getBillingRecommendation(companyId, accessToken);
                if (!cancelled) {
                    setRecommendation(data);
                }
            } catch (error) {
                console.error('[useBillingRecommendation] Failed to load:', error);
                if (!cancelled) {
                    setRecommendation(null);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [companyId, accessToken]);

    const isMicrosoftTenant =
        recommendation?.recommended_provider === 'microsoft' || !!sessionMicrosoftTenantId;

    // Entreprise SSO Microsoft : toujours orienter vers Marketplace (l’URL peut être absente en config)
    const isMicrosoftRecommended = isMicrosoftTenant;

    const openMarketplace = useCallback(() => {
        const url = recommendation?.marketplace_url?.trim();
        if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    }, [recommendation?.marketplace_url]);

    return {
        recommendation,
        loading,
        isMicrosoftTenant,
        isMicrosoftRecommended,
        marketplaceUrl: recommendation?.marketplace_url,
        openMarketplace,
    };
}
